/**
 * Customer Edit Basic Info Modal — zod schema.
 *
 * board2-#79 「地址格式改成一個textfield，丟到googlet拆分」
 * (page 2cb89adb-b34d-80ee-a729-d094a38c45cc): registered-address required
 * set is country + postcode + address (single full-address textfield).
 * stateId/cityId are UNVALIDATED passthrough — backend-parse OUTPUTS
 * (Google Maps split, simulated by the mocks). Supersedes the legacy
 * "all 4 registered-address fields required" rule (repo CLAUDE.md) which
 * predates the single-textfield entry group.
 *
 * Pure module (no react/antd deps) so the vitest contract suite can import
 * it in the node environment — mirrors vendors/editBasicInfoFormSchema.
 */

import { z } from 'zod';

export const editCustomerBasicInfoFormSchema = z.object({
  // Basic Information
  email: z
    .string()
    .min(1, 'customer.validation.emailRequired')
    .email('customer.validation.invalidEmail'),
  web: z.string().optional().or(z.literal('')),
  phoneCountryCode: z.string().min(1, 'customer.validation.countryCodeRequired'),
  phoneNumber: z.string().min(1, 'customer.validation.phoneRequired'),
  phoneExtension: z.string().optional().or(z.literal('')),
  fax: z.string().optional().or(z.literal('')),

  // Registered Location — required set (board2-#79)
  countryId: z
    .number()
    .nullable()
    .refine((v) => v !== null && v > 0, 'customer.validation.countryRequired'),
  postcode: z.string().min(1, 'customer.validation.postcodeRequired'),
  address: z.string().min(1, 'customer.validation.addressRequired'),

  // Backend-parse OUTPUTS — passthrough only, never validated in the form
  stateId: z.number().nullable(),
  stateName: z.string(),
  cityId: z.number().nullable(),
  cityName: z.string(),

  // Additional Information (passthrough so the resolver does not strip them)
  internalContactUserId: z.number().nullable().optional(),
  parentCompanyId: z.number().nullable().optional(),

  // Notes (separate section)
  accountNotes: z.string().max(1000, 'customer.validation.notesMax').optional().or(z.literal('')),
  deliveryNotes: z.string().max(1000, 'customer.validation.notesMax').optional().or(z.literal('')),
  salesNotes: z.string().max(1000, 'customer.validation.notesMax').optional().or(z.literal('')),
});

export type EditCustomerBasicInfoFormData = z.infer<typeof editCustomerBasicInfoFormSchema>;
